import * as Sentry from '@sentry/react';
import {
  useCallback,
  useState,
  type Dispatch,
  type SetStateAction,
} from 'react';

import { getErrorMessage } from '@/hooks/api/errorHelpers';
import { useToast } from '@/hooks/use-toast';
import type { slackSettings } from '@kirin/api-client/contracts';

interface UseSlackIntegrationControllerOptions {
  saveSettings: (
    data: slackSettings.UpsertSlackSettingsBody
  ) => Promise<slackSettings.UpsertSlackSettingsResponse>;
  deleteSettings: () => Promise<unknown>;
  sentryTags: { save: string; delete: string };
  sentryExtra?: Record<string, unknown>;
}

export interface SlackIntegrationController {
  connectOpen: boolean;
  setConnectOpen: Dispatch<SetStateAction<boolean>>;
  disconnectOpen: boolean;
  openConnect: () => void;
  openDisconnect: () => void;
  closeDisconnect: () => void;
  handleSave: (
    data: slackSettings.UpsertSlackSettingsBody
  ) => Promise<slackSettings.UpsertSlackSettingsResponse>;
  handleConfirmDisconnect: () => Promise<void>;
  handleRequestDisconnect: () => void;
}

export function useSlackIntegrationController(
  options: UseSlackIntegrationControllerOptions
): SlackIntegrationController {
  const { saveSettings, deleteSettings, sentryTags, sentryExtra } = options;
  const { toast } = useToast();

  const [connectOpen, setConnectOpen] = useState(false);
  const [disconnectOpen, setDisconnectOpen] = useState(false);

  const openConnect = useCallback((): void => setConnectOpen(true), []);
  const openDisconnect = useCallback((): void => setDisconnectOpen(true), []);
  const closeDisconnect = useCallback((): void => setDisconnectOpen(false), []);

  const captureError = useCallback(
    (error: unknown, tag: string): void => {
      Sentry.captureException(error, {
        tags: { error_type: tag },
        ...(sentryExtra !== undefined ? { extra: sentryExtra } : {}),
      });
    },
    [sentryExtra]
  );

  const handleSave = useCallback(
    async (
      data: slackSettings.UpsertSlackSettingsBody
    ): Promise<slackSettings.UpsertSlackSettingsResponse> => {
      try {
        const result = await saveSettings(data);
        toast({
          title: 'Settings saved',
          description: result.testMessageSent
            ? 'Test message sent to Slack successfully!'
            : 'Slack notification settings updated',
        });
        return result;
      } catch (error) {
        captureError(error, sentryTags.save);
        toast({
          title: 'Error saving settings',
          description: getErrorMessage(error),
          variant: 'destructive',
        });
        throw error;
      }
    },
    [saveSettings, toast, captureError, sentryTags.save]
  );

  const handleDelete = useCallback(async (): Promise<void> => {
    try {
      await deleteSettings();
      toast({
        title: 'Settings deleted',
        description: 'Slack notification settings have been removed',
      });
    } catch (error) {
      captureError(error, sentryTags.delete);
      toast({
        title: 'Error deleting settings',
        description: getErrorMessage(error),
        variant: 'destructive',
      });
      throw error;
    }
  }, [deleteSettings, toast, captureError, sentryTags.delete]);

  const handleConfirmDisconnect = useCallback(async (): Promise<void> => {
    try {
      await handleDelete();
      setDisconnectOpen(false);
      setConnectOpen(false);
    } catch {
      // Toast surfaced by handleDelete; keep dialog mounted so user can retry.
    }
  }, [handleDelete]);

  const handleRequestDisconnect = useCallback((): void => {
    setConnectOpen(false);
    setDisconnectOpen(true);
  }, []);

  return {
    connectOpen,
    setConnectOpen,
    disconnectOpen,
    openConnect,
    openDisconnect,
    closeDisconnect,
    handleSave,
    handleConfirmDisconnect,
    handleRequestDisconnect,
  };
}
